import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress, Stack, IconButton, FormControlLabel, Switch } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  createTaskClassification,
  deleteTaskClassificationById,
  getTaskClassificationList,
  updateTaskClassificationById,
} from '../../../../services/taskClassification';
import { useStyles } from '../styles/projectTaskClassificationStyles';
import AddTaskClassificationModal from './popups/AddTaskClassificationModal';
import { DeleteModal } from './popups';
import { useProject } from '../../Layout/header/context';
import { isStakeholder } from '../../../../utils/share';

const ProjectTaskClassification = ({ projectId, isPermissionUpdateTaskClassifications }) => {
  const classes = useStyles();
  const { stakeholderOverviewVisibility, updateOverviewVisibility, canConfigureStakeholderVisibility } = useProject();
  const [taskClassifications, setTaskClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [isVisibilityUpdating, setIsVisibilityUpdating] = useState(false);

  const handleVisibilityToggle = async (event) => {
    try {
      setIsVisibilityUpdating(true);
      await updateOverviewVisibility({ taskClassification: event.target.checked });
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        'An error occurred while updating stakeholder visibility. Please try again later.';
      toast.error(errMsg);
    } finally {
      setIsVisibilityUpdating(false);
    }
  };

  const fetchTaskClassifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTaskClassificationList({ projectId });
      setTaskClassifications(response?.data?.data?.rows || []);
    } catch (error) {
      toast.error('Failed to fetch task classifications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchTaskClassifications();
    }
  }, [projectId, fetchTaskClassifications]);

  // Handle modal open/close
  const handleModalOpen = (isEdit = false, classification = null) => {
    setIsEditMode(isEdit);
    setSelectedClassification(classification);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedClassification(null);
  };

  // Handle adding or updating task classification
  const handleAddOrUpdateTaskClassification = async (classification) => {
    try {
      const payload = {
        name: classification.name,
        projectId,
        plannedStartDate: classification.plannedStartDate || null,
        plannedEndDate: classification.plannedEndDate || null,
        actualStartDate: classification.actualStartDate || null,
        actualEndDate: classification.actualEndDate || null,
        remark: classification.remark || null,
        status: classification.status || null,
      };

      if (isEditMode && selectedClassification) {
        // Update logic
        const response = await updateTaskClassificationById(payload, selectedClassification.id);
        toast.success(response?.data?.message);
      } else {
        // Create logic
        const response = await createTaskClassification(payload);
        toast.success(response?.data?.message);
      }

      await fetchTaskClassifications();
      handleModalClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'An error occurred');
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedClassification) {
      try {
        const response = await deleteTaskClassificationById(selectedClassification.id);
        toast.success(response?.data?.message);
        await fetchTaskClassifications();
        setSelectedClassification(null);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || 'Failed to delete classification');
      }
      setOpenDelete(false);
    }
  };

  if (loading && !taskClassifications.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const AddButton = () => (
    <Box
      className={classes.classificationButton}
      onClick={isPermissionUpdateTaskClassifications ? () => handleModalOpen(false) : null}
    >
      <Box className={classes.addIcon}>
        <Add />
      </Box>
      <Typography variant="body2" className={classes.buttonText}>
        Add Task Classification Type
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h4" color="GrayText">
          Task Classifications
        </Typography>
        {canConfigureStakeholderVisibility && (
          <FormControlLabel
            sx={{ mr: 0, ml: 0 }}
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={stakeholderOverviewVisibility?.taskClassification !== false}
                onChange={handleVisibilityToggle}
                disabled={isVisibilityUpdating}
              />
            }
            label={<Typography variant="caption">Stakeholder</Typography>}
          />
        )}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
        <AddButton />
        {taskClassifications.map((classification) => (
          <Box key={classification.id} display="flex" alignItems="center" className={classes.classificationButton}>
            <Box sx={{ flexGrow: 1, marginRight: 1 }}>
              <Typography variant="body2" className={classes.classificationName}>
                {classification.name}
              </Typography>
              <Stack sx={{ marginTop: '5px' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Start:{' '}
                  {classification.plannedStartDate
                    ? new Date(classification.plannedStartDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  End:{' '}
                  {classification.plannedEndDate
                    ? new Date(classification.plannedEndDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </Typography>
              </Stack>
            </Box>

            <Box display="flex" alignItems="center">
              {!isStakeholder() && (
                <>
                  <IconButton
                    disabled={!isPermissionUpdateTaskClassifications}
                    size="small"
                    onClick={() => handleModalOpen(true, classification)} // Open in edit mode
                    className={classes.editButton}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    disabled={!isPermissionUpdateTaskClassifications}
                    size="small"
                    onClick={() => {
                      setSelectedClassification(classification);
                      setOpenDelete(true); // Open delete confirmation modal
                    }}
                    className={classes.deleteButton}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Task Classification Modal */}
      <AddTaskClassificationModal
        open={modalOpen}
        projectId={projectId}
        classification={selectedClassification} // Pass selected classification for edit
        onClose={handleModalClose}
        onSubmit={handleAddOrUpdateTaskClassification}
        isEditMode={isEditMode} // Pass edit mode flag
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal open={openDelete} onClose={() => setOpenDelete(false)} onDelete={handleConfirmDelete} />
    </Stack>
  );
};

ProjectTaskClassification.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPermissionUpdateTaskClassifications: PropTypes.bool,
};

export default ProjectTaskClassification;
